import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Layout
import DashboardLayout from "./components/layout/DashboardLayout";

// Auth Pages
import LoginPage from "./pages/auth/LoginPage";
import OnboardingPage from "./pages/customer/OnboardingPage";
import RegisterPage from "./pages/auth/RegisterPage";

// Dashboard Pages
import CustomerDashboard from "./pages/dashboard/CustomerDashboard";
import AdminDashboard from "./pages/dashboard/AdminDashboard";

// Reports Pages
import ReportsPage from "./pages/reports/ReportsPage";
import ComparisonPage from "./pages/reports/ComparisonPage";
import ReportDetailPage from "./pages/reports/ReportDetailPage";

// Channel Pages
import LinkedInPage from "./pages/channels/LinkedInPage";
import EmailPage from "./pages/channels/EmailPage";
import AdsPage from "./pages/channels/AdsPage";
import InboundPage from "./pages/channels/InboundPage";

// Customer Pages
import CustomerDetailsPage from "./pages/customer/CustomerDetailsPage";
import DocumentsPage from "./pages/customer/DocumentsPage";

// Journey Page
import ABMJourneyPage from "./pages/journey/ABMJourneyPage";

// Admin Pages
import AdminCustomersPage from "./pages/admin/AdminCustomersPage";
import AdminReportsPage from "./pages/admin/AdminReportsPage";
import AdminOverallMetricsPage from "./pages/admin/AdminOverallMetricsPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
    const { isAuthenticated, loading, user } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-surface-app flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-brand-red/30 border-t-brand-red rounded-full animate-spin" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(user?.role)) {
        return <Navigate to={user?.role === 'admin' ? '/admin' : '/dashboard'} replace />;
    }

    // Onboarding gate for customers who haven't onboarded
    if (user?.role === 'customer' && user?.is_onboarded === false) {
        return <Navigate to="/onboarding" replace />;
    }

    return children;
};

// Public Route Component (redirects if already logged in)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading, user } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-surface-app flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-brand-red/30 border-t-brand-red rounded-full animate-spin" />
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to={user?.role === 'admin' ? '/admin' : '/dashboard'} replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/login" element={
                <PublicRoute>
                    <LoginPage />
                </PublicRoute>
            } />
            <Route path="/register" element={
                <PublicRoute>
                    <RegisterPage />
                </PublicRoute>
            } />

            {/* Onboarding Route */}
            <Route path="/onboarding" element={<OnboardingPage />} />

            {/* Customer Routes */}
            <Route path="/" element={
                <ProtectedRoute allowedRoles={['customer']}>
                    <DashboardLayout />
                </ProtectedRoute>
            }>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<CustomerDashboard />} />
                <Route path="journey" element={<ABMJourneyPage />} />
                <Route path="reports/weekly" element={<ReportsPage reportType="weekly" />} />
                <Route path="reports/monthly" element={<ReportsPage reportType="monthly" />} />
                <Route path="reports/compare" element={<ComparisonPage />} />
                <Route path="reports/:reportId" element={<ReportDetailPage />} />
                <Route path="channels/linkedin" element={<LinkedInPage />} />
                <Route path="channels/email" element={<EmailPage />} />
                <Route path="channels/ads" element={<AdsPage />} />
                <Route path="channels/inbound" element={<InboundPage />} />
                <Route path="customer-details" element={<CustomerDetailsPage />} />
                <Route path="documents" element={<DocumentsPage />} />
            </Route>

            {/* Admin Routes */}
            <Route path="/admin" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <DashboardLayout />
                </ProtectedRoute>
            }>
                <Route index element={<AdminDashboard />} />
                <Route path="customers" element={<AdminCustomersPage />} />
                <Route path="reports" element={<AdminReportsPage />} />
                <Route path="overall-metrics" element={<AdminOverallMetricsPage />} />
            </Route>

            {/* Catch all - redirect to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <AppRoutes />
                    <Toaster 
                        position="top-right"
                        toastOptions={{
                            style: {
                                background: '#121212',
                                border: '1px solid #27272A',
                                color: '#FFFFFF',
                            },
                        }}
                    />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
